import React, { useState } from "react"
import { Button, Card, Space, Typography } from "antd"
import { EyeOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import DataTable from "../../../components/DataTable/DataTable"
import { SurveyAnswer } from "../../../../domain/entities/SurveyAnswer"
import { SurveysViewModel } from "../SurveysViewModel"
import DataTableRowsSkeleton from "../../../styledComponents/LoadingSkeletons/DataTableRowsSkeleton"
import SurveyModal from "./SurveyModal"

const { Text, Title } = Typography

interface SurveyAnswersGroupedTableProps {
	data: SurveyAnswer[]
	sections?: any[]
	viewModel: SurveysViewModel
}

interface GroupedUser {
	email: string
	name: string
	totalAnswers: number
	lastSubmitted: string
	answers: SurveyAnswer[]
}

const SurveyAnswersGroupedTable = ({ data, sections, viewModel }: SurveyAnswersGroupedTableProps) => {
	const { t } = useTranslation("surveys", { keyPrefix: "form.answers" })
	const [modalVisible, setModalVisible] = useState(false)
	const [selectedUser, setSelectedUser] = useState<GroupedUser | null>(null)

	const findQuestionById = (questionId: number) => {
		if (!sections) return null

		for (const section of sections) {
			if (section.questions) {
				const question = section.questions.find((q: any) => q.id === questionId)
				if (question) return question
			}
		}
		return null
	}

	const groupAnswersByUser = (): GroupedUser[] => {
		const groupedMap = new Map<string, GroupedUser>()

		data.forEach(answer => {
			const email = answer.email || "usuario@desconocido.com"
			const name = email.split("@")[0] || "Usuario"
			const submittedDate = answer.submittedAt
				? typeof answer.submittedAt === "string"
					? answer.submittedAt
					: answer.submittedAt.toISOString()
				: ""

			if (!groupedMap.has(email)) {
				groupedMap.set(email, {
					email,
					name,
					totalAnswers: 0,
					lastSubmitted: submittedDate,
					answers: []
				})
			}

			const group = groupedMap.get(email)!
			group.answers.push(answer)
			group.totalAnswers = group.answers.length

			if (submittedDate && submittedDate > group.lastSubmitted) {
				group.lastSubmitted = submittedDate
			}
		})

		return Array.from(groupedMap.values()).sort(
			(a, b) => new Date(b.lastSubmitted).getTime() - new Date(a.lastSubmitted).getTime()
		)
	}

	const handleViewResponses = (user: GroupedUser) => {
		setSelectedUser(user)
		setModalVisible(true)
	}

	const groupedUsers = groupAnswersByUser()

	const columns = [
		{
			title: t("name"),
			dataIndex: "name",
			key: "name",
			sorter: true,
			width: "12rem"
		},
		{
			title: t("email"),
			dataIndex: "email",
			key: "email",
			sorter: true,
			width: "15rem"
		},
		{
			title: t("totalAnswers"),
			dataIndex: "totalAnswers",
			key: "totalAnswers",
			sorter: true,
			width: "8rem",
			align: "center" as const
		},
		{
			title: t("lastSubmitted"),
			dataIndex: "lastSubmitted",
			key: "lastSubmitted",
			sorter: true,
			render: (date: string) => (date ? new Date(date).toLocaleDateString() : "N/A"),
			width: "10rem"
		},
		{
			title: t("actions"),
			key: "actions",
			width: "8rem",
			align: "center" as const,
			render: (_: any, user: GroupedUser) => (
				<Button type="primary" icon={<EyeOutlined />} size="small" onClick={() => handleViewResponses(user)}>
					{t("viewResponses")}
				</Button>
			)
		}
	]

	const renderUserResponses = () => {
		if (!selectedUser?.answers?.length) {
			return (
				<Card>
					<Text type="secondary">{t("noAnswersFound")}</Text>
				</Card>
			)
		}

		return (
			<div style={{ width: "100%" }}>
				{selectedUser.answers.map((answer, index) => {
					let questionText = answer.question?.text
					if (!questionText && answer.questionId) {
						const foundQuestion = findQuestionById(answer.questionId)
						questionText = foundQuestion?.text || foundQuestion?.questionText
					}

					const answerText = answer.textAnswer ?? answer.selectedOption?.text ?? "Sin respuesta"
					const submittedDate = answer.submittedAt
						? new Date(
								typeof answer.submittedAt === "string" ? answer.submittedAt : answer.submittedAt
						  ).toLocaleDateString()
						: "N/A"

					return (
						<div
							key={answer.id || index}
							style={{
								padding: "12px",
								border: "1px solid #f0f0f0",
								borderRadius: "4px",
								backgroundColor: "#fafafa",
								marginBottom: "12px"
							}}
						>
							<div style={{ marginBottom: "8px" }}>
								<Text strong>{t("question")}:</Text>
								<div style={{ marginTop: "4px" }}>
									<Text>{questionText || "Pregunta no disponible"}</Text>
								</div>
							</div>

							<div>
								<Text strong>{t("answer")}:</Text>
								<div style={{ marginTop: "4px" }}>
									<Text>{answerText}</Text>
								</div>
							</div>

							<div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
								<Text type="secondary" style={{ fontSize: "12px" }}>
									{t("submittedAt")}: {submittedDate}
								</Text>
							</div>
						</div>
					)
				})}
			</div>
		)
	}

	return (
		<>
			<DataTable
				tableConfig={{
					sort: viewModel.tableConfig.sort,
					pageSize: viewModel.tableConfig.pageSize
				}}
				renderCustomLoadingComponent={() => <DataTableRowsSkeleton size="lg" />}
				onChange={data => {
					data.sorter &&
						viewModel.setTableConfig({ sort: { order: data.sorter.order, field: data.sorter.field } })
				}}
				columns={columns}
				paginationConfig={{
					position: ["bottomCenter"]
				}}
				dataSource={groupedUsers.map((user, index) => ({
					key: user.email,
					...user
				}))}
			/>

			<SurveyModal
				title={
					<Space>
						<EyeOutlined />
						<span>
							{t("userResponses")}: {selectedUser?.name} ({selectedUser?.email})
						</span>
					</Space>
				}
				open={modalVisible}
				onCancel={() => {
					setModalVisible(false)
					setSelectedUser(null)
				}}
				width={900}
				maxHeight={500}
			>
				{renderUserResponses()}
			</SurveyModal>
		</>
	)
}

export default SurveyAnswersGroupedTable
